/**
 * CRYIN — Product Component JS
 * Carousel and product form custom elements.
 * Bottom sheet is initialized by spa-router.js.
 */

(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     Product Carousel
     ----------------------------------------------------------------------- */

  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      // Tear down any previous listeners before re-binding
      this.destroy();

      this.track = this.querySelector('[data-carousel-track]');
      this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.dots = [...(this.closest('.pdp__snap-panel') || this.parentElement).querySelectorAll('[data-carousel-dot]')];
      this.currentIndex = 0;
      this.total = this.slides.length;

      // Drag state
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.dragDelta = 0;

      if (this.total <= 1) return;

      this.bindEvents();
      this.goTo(0);
    }

    disconnectedCallback() {
      this.destroy();
    }

    /**
     * Remove all bound listeners so connectedCallback can safely re-bind.
     */
    destroy() {
      if (this._onWindowMouseMove) {
        window.removeEventListener('mousemove', this._onWindowMouseMove);
      }
      if (this._onWindowMouseUp) {
        window.removeEventListener('mouseup', this._onWindowMouseUp);
      }
      // AbortController cleans up all element-scoped listeners
      if (this._ac) {
        this._ac.abort();
      }
    }

    bindEvents() {
      // Use an AbortController so every listener can be removed at once
      this._ac = new AbortController();
      const signal = this._ac.signal;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev(), { signal });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next(), { signal });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.carouselDot, 10);
          this.goTo(index);
        }, { signal });
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      }, { signal });

      // Mouse drag events
      this.track.addEventListener('mousedown', (e) => this.onDragStart(e.clientX, e), { signal });

      // Window-level listeners need manual removal (AbortController on window not always safe)
      this._onWindowMouseMove = (e) => this.onDragMove(e.clientX);
      this._onWindowMouseUp = () => this.onDragEnd();
      window.addEventListener('mousemove', this._onWindowMouseMove);
      window.addEventListener('mouseup', this._onWindowMouseUp);

      // Touch events
      this.track.addEventListener('touchstart', (e) => this.onDragStart(e.touches[0].clientX), { passive: true, signal });
      this.track.addEventListener('touchmove', (e) => this.onDragMove(e.touches[0].clientX), { passive: true, signal });
      this.track.addEventListener('touchend', () => this.onDragEnd(), { signal });

      // Prevent image drag
      this.track.querySelectorAll('img').forEach(img => {
        img.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
      });

      // Horizontal trackpad / mousepad wheel support
      this._wheelTimeout = null;
      this._wheelLocked = false;
      this.addEventListener('wheel', (e) => {
        // Only act on horizontal scroll (trackpad swipe)
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 15) {
          e.preventDefault();
          if (this._wheelLocked) return;
          this._wheelLocked = true;
          if (e.deltaX > 0) {
            this.next();
          } else {
            this.prev();
          }
          // Debounce — lock for 500ms to prevent rapid-fire
          clearTimeout(this._wheelTimeout);
          this._wheelTimeout = setTimeout(() => { this._wheelLocked = false; }, 500);
        }
      }, { signal, passive: false });
    }

    onDragStart(x, e) {
      this.isDragging = true;
      this.startX = x;
      this.dragDelta = 0;
      this.track.classList.add('is-dragging');
      this.classList.add('is-dragging');
      if (e && e.preventDefault) e.preventDefault();
    }

    onDragMove(x) {
      if (!this.isDragging) return;
      this.dragDelta = x - this.startX;
      const trackWidth = this.track.offsetWidth;
      const baseOffset = -this.currentIndex * 100;
      const dragPercent = (this.dragDelta / trackWidth) * 100;
      this.track.style.transform = `translateX(${baseOffset + dragPercent}%)`;
    }

    onDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      this.classList.remove('is-dragging');

      const threshold = this.track.offsetWidth * 0.15;
      if (this.dragDelta < -threshold) {
        this.next();
      } else if (this.dragDelta > threshold) {
        this.prev();
      } else {
        this.goTo(this.currentIndex);
      }
      this.dragDelta = 0;
    }

    prev() {
      this.goTo(this.currentIndex > 0 ? this.currentIndex - 1 : this.total - 1);
    }

    next() {
      this.goTo(this.currentIndex < this.total - 1 ? this.currentIndex + 1 : 0);
    }

    goTo(index) {
      this.currentIndex = index;
      this.track.style.transform = `translateX(-${index * 100}%)`;

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('pdp__dot--active', i === index);
      });

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('pdp__slide--active', i === index);
      });
    }
  }

  if (!customElements.get('product-carousel')) {
    customElements.define('product-carousel', ProductCarousel);
  }

  /* -----------------------------------------------------------------------
     Product Form — AJAX Add to Cart (single-variant products only)
     For multi-variant products, the bottom sheet handles ATC.
     ----------------------------------------------------------------------- */

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form[data-product-form]');
      if (this.form) {
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
      }
    }

    async onSubmit(e) {
      e.preventDefault();

      const btn = this.form.querySelector('[data-add-to-cart]');
      if (!btn || btn.disabled) return;
      if (btn.hasAttribute('data-open-sizes')) return;

      btn.disabled = true;

      try {
        const formData = new FormData(this.form);
        const response = await fetch(window.CRYIN.routes.cartAdd + '.js', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) throw new Error('Add to cart failed');

        // Visual feedback — + rotates to ✓
        btn.classList.add('is-rotating');
        setTimeout(() => {
          btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>';
          btn.classList.remove('is-rotating');
          btn.style.transform = 'scale(1.1)';
          setTimeout(() => { btn.style.transform = ''; }, 200);
        }, 200);

        // Fly ghost to cart
        if (typeof flyToCart === 'function') {
          flyToCart();
        } else if (window.CRYIN && window.CRYIN.flyToCart) {
          window.CRYIN.flyToCart();
        }

        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
        }, 1200);

        const cartResponse = await fetch(window.CRYIN.routes.cart + '.js');
        const cart = await cartResponse.json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });

      } catch (error) {
        btn.disabled = false;
      }
    }
  }

  if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductForm);
  }

})();
